import React, { useMemo, useState, useEffect } from 'react';
import LoadingState from './LoadingState';
import ListeningState from './ListeningState';
import SpeakingState from './SpeakingState';
import IdleState from './IdleState';
import './styles.css';

/**
 * AIStatusIndicator - Main component that renders the appropriate state component
 * @param {string} status - Current AI status: 'loading', 'listening', 'speaking', or 'idle'
 * @param {boolean} muted - Whether the microphone is muted (only affects mic button, not status display)
 * @param {function} onMicPress - Callback function for microphone button press
 */
export default function AIStatusIndicator({ status, muted, onMicPress }) {
  const [micLevel, setMicLevel] = useState(0);

  // Poll microphone level from global control
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.gptRealtimeControl?.getMicLevel && !muted) {
        const level = window.gptRealtimeControl.getMicLevel();
        setMicLevel(level);
      } else {
        setMicLevel(0);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [muted]);

  const StateComponent = useMemo(() => {
    switch (status) {
      case 'loading':
        return LoadingState;
      case 'listening':
        return ListeningState;
      case 'speaking':
        return SpeakingState;
      case 'idle':
      default:
        return IdleState;
    }
  }, [status]);

  const getAnimationIntensity = () => {
    if (muted || micLevel < 5) return 0;
    return Math.min(100, micLevel);
  };

  const animationIntensity = getAnimationIntensity();

  return (
    <div className="ai-status-container">
      <div className="ai-visual-area">
        <StateComponent muted={muted} />
      </div>
      
      <button 
        onClick={onMicPress} 
        className={`ai-mic-btn margin-0 ${animationIntensity > 10 ? 'ai-mic-active' : ''}`}
        style={{
          transform: `scale(${1 + (animationIntensity / 100) * 0.2})`,
          filter: `drop-shadow(0 0 ${4 + animationIntensity * 0.15}px rgba(34, 197, 94, ${animationIntensity / 200}))`,
          transition: 'transform 0.1s ease-out, filter 0.1s ease-out'
        }}
        aria-label={muted ? 'Activar micrófono' : 'Desactivar micrófono'}
      >
        <img
          src={muted ? '/assets/micro-offf_chat.png' : '/assets/micro-home.png'}
          alt={muted ? 'Mic apagado' : 'Mic encendido'}
          className="ai-mic-icon "
          style={{
            filter: `brightness(${1 + (animationIntensity / 100) * 0.3})`,
            transition: 'filter 0.1s ease-out'
          }}
        />
        
        {!muted && animationIntensity > 15 && (
          <>
            <div 
              className="ai-mic-pulse-ring ai-mic-ring-1"
              style={{ 
                opacity: Math.min(0.8, animationIntensity / 50),
                animationDuration: `${1.2 - (animationIntensity / 100) * 0.4}s`
              }}
            />
            {animationIntensity > 40 && (
              <div 
                className="ai-mic-pulse-ring ai-mic-ring-2"
                style={{ 
                  opacity: Math.min(0.6, (animationIntensity - 20) / 60),
                  animationDuration: `${1.0 - (animationIntensity / 100) * 0.3}s`
                }}
              />
            )}
            {animationIntensity > 70 && (
              <div 
                className="ai-mic-pulse-ring ai-mic-ring-3"
                style={{ 
                  opacity: Math.min(0.4, (animationIntensity - 50) / 50),
                  animationDuration: `${0.8 - (animationIntensity / 100) * 0.2}s`
                }}
              />
            )}
          </>
        )}
      </button>
    </div>
  );
}
